import { Alert, AlertColor, Box, SnackbarProps as MUISnackbarProps, SnackbarContent, Tooltip } from '@mui/material';
import { IconButton } from 'design-system';
import React, { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { MdContentCopy } from 'react-icons/md';
import { extractTraceIdFromMessage } from 'utils/traceid';
import useIsViewport from './useIsViewport';

export enum SnackbarVariant {
  DEFAULT = 'default',
  ERROR = 'error',
  ALERT = 'alert',
}

export interface SnackbarProps extends MUISnackbarProps {
  icon?: ReactNode;
}

const SnackbarContext = createContext<
  | {
      showSnackbar: (props: SnackbarProps, variant?: SnackbarVariant) => number;
      hideSnackbar: () => void;
      hideOldestSnackbar: () => void;
      dismissSnackbar: (id: number) => void;
    }
  | undefined
>(undefined);

SnackbarContext.displayName = 'SnackbarContext';

const DEFAULT_AUTO_HIDE = 6000;
const SNACKBAR_GAP = 1;
const MAX_STACK_HEIGHT_VH = 50;
const MAX_SNACKBARS = 2;

const getErrorSeverity = (message: string | ReactNode): AlertColor => {
  if (typeof message !== 'string') return 'error';
  if (message.toLowerCase().includes('something went wrong')) return 'error';
  return 'warning';
};

interface QueueItem {
  id: number;
  props: SnackbarProps;
  variant: SnackbarVariant;
}

interface SingleSnackbarProps {
  item: QueueItem;
  onRemove: (id: number) => void;
  onCopyTraceId?: (traceId: string) => void;
}

const SingleSnackbar = React.memo(function SingleSnackbar({ item, onRemove, onCopyTraceId }: SingleSnackbarProps) {
  const { id, props, variant } = item;
  const message = props?.message;
  const traceId = typeof message === 'string' ? extractTraceIdFromMessage(message) : null;
  const duration = props?.autoHideDuration ?? DEFAULT_AUTO_HIDE;

  const handleClose = useCallback(() => {
    onRemove(id);
  }, [id, onRemove]);

  const handleCopyTraceId = useCallback(() => {
    if (!traceId) return;
    void navigator.clipboard.writeText(traceId);
    onCopyTraceId?.(traceId);
  }, [traceId, onCopyTraceId]);

  React.useEffect(() => {
    if (duration <= 0) return;
    const t = setTimeout(handleClose, duration);
    return () => clearTimeout(t);
  }, [duration, handleClose]);

  return (
    <Box
      sx={{
        flexShrink: 0,
        transition: 'transform 0.2s ease-out, opacity 0.2s ease-out',
        width: '100%',
        maxWidth: { xs: '100%', sm: 400 },
      }}
    >
      {variant === SnackbarVariant.ALERT || variant === SnackbarVariant.ERROR ? (
        <Alert
          icon={props?.icon ?? undefined}
          severity={
            props?.color
              ? (props?.color as AlertColor)
              : variant === SnackbarVariant.ERROR
                ? getErrorSeverity(props?.message)
                : 'info'
          }
          onClose={handleClose}
          sx={{ width: '100%', alignItems: 'center' }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap' }}>
            <span style={{ minWidth: 0 }}>{message}</span>
            {traceId && (
              <Tooltip title="Copy trace ID">
                <IconButton
                  size="small"
                  color="inherit"
                  onClick={handleCopyTraceId}
                  sx={{ padding: 0.5, flexShrink: 0 }}
                  aria-label="Copy trace ID"
                >
                  <MdContentCopy style={{ fontSize: 20 }} />
                </IconButton>
              </Tooltip>
            )}
          </span>
        </Alert>
      ) : (
        <SnackbarContent
          sx={{
            textAlign: 'center',
            justifyContent: 'center',
            display: 'flex',
          }}
          message={props?.message}
        />
      )}
    </Box>
  );
});

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<QueueItem[]>([]);
  const idRef = useRef(0);
  const isMobile = useIsViewport('md');

  const showSnackbar = useCallback((props: SnackbarProps, variant = SnackbarVariant.DEFAULT) => {
    const message = props?.message;
    const messageKey = typeof message === 'string' ? message : undefined;

    idRef.current += 1;
    const id = idRef.current;

    setQueue((q) => {
      if (messageKey !== undefined) {
        const isDuplicate = q.some(
          (item) =>
            item.variant === variant && typeof item.props?.message === 'string' && item.props.message === messageKey,
        );
        if (isDuplicate) return q;
      }
      const next = [...q, { id, props, variant }];
      return next.length > MAX_SNACKBARS ? next.slice(-MAX_SNACKBARS) : next;
    });

    return id;
  }, []);

  const hideSnackbar = useCallback(() => {
    setQueue((q) => (q.length === 0 ? q : q.slice(0, -1)));
  }, []);

  const hideOldestSnackbar = useCallback(() => {
    setQueue((q) => (q.length === 0 ? q : q.slice(1)));
  }, []);

  const removeById = useCallback((id: number) => {
    setQueue((q) => q.filter((x) => x.id !== id));
  }, []);

  const handleCopyTraceId = useCallback(() => {
    // Optional: analytics or logging when user copies trace ID
  }, []);

  const contextValue = useMemo(
    () => ({ showSnackbar, hideSnackbar, hideOldestSnackbar, dismissSnackbar: removeById }),
    [showSnackbar, hideSnackbar, hideOldestSnackbar, removeById],
  );

  // Render newest first (DOM order) so column-reverse puts newest at bottom
  const ordered = useMemo(() => [...queue].reverse(), [queue]);

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}

      {queue.length > 0 && (
        <Box
          aria-live="polite"
          role="region"
          aria-label="Notifications"
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            display: 'flex',
            flexDirection: 'column-reverse',
            alignItems: 'center',
            gap: SNACKBAR_GAP,
            maxHeight: `min(${MAX_STACK_HEIGHT_VH}vh, 400px)`,
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: isMobile ? 7 : 2,
            paddingLeft: 2,
            paddingRight: 2,
            paddingTop: 2,
            zIndex: (theme) => theme.zIndex.snackbar,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column-reverse',
              alignItems: 'center',
              gap: SNACKBAR_GAP,
              width: '100%',
              maxWidth: { sm: 400 },
            }}
          >
            {ordered.map((item) => (
              <SingleSnackbar key={item.id} item={item} onRemove={removeById} onCopyTraceId={handleCopyTraceId} />
            ))}
          </Box>
        </Box>
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
